import {
  throwError as observableThrowError,
  of as observableOf,
  Subject,
} from "rxjs";

import { share, tap, catchError, map } from "rxjs/operators";
import { ErrorHandler, Injectable, Injector } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { WindowRef } from "../window/WindowRef";
import { environment } from "../../environments/environment";
import * as Rollbar from "rollbar";
import { RollbarService } from "../error-handler";

import { studentDataSearch } from "../store/students/state";
import { flatMap } from "rxjs/operators";

@Injectable()
export class KpiService {
  custId = "9999";
  title = "";
  errorMessage;
  studentInfo = [];
  studentInfoPendingObservable;
  schoolInfo = [];
  schoolInfoPendingObservable;
  userInfo;
  userInfoPendingObservable;
  schoolKpiData;
  schoolKpiDataPendingObservable;
  studentsDemographicFilter;
  selectedDemographic;
  currentPage;
  selectedSchoolIdInSchoolProfile: number;
  selectedSchoolLevel: string;
  selectedSchoolRegion: string;
  selectedSchoolDemographic;
  selectedSchoolFilter: string;
  selectedStudentIdInStudentProfile: number;
  schoolPageNumber: number;
  schoolPageSize: number;

  loadingSchoolData;
  initializedLDClient;

  constructor(
    public http: HttpClient,
    private winRef: WindowRef,
    private rollbarErrorHandler: ErrorHandler,
    private injector: Injector
  ) {}

  showError(error, msg1, msg2) {
    // if starter, then open RL's live chat instead of redirecting to SZ support
    // in this case, we need to fill the last part of the error message in the HTML component itself
    // because otherwise Angular sanitizes out the Live Chat loader (to prevent injection attacks)
    if (error === 403) {
      this.errorMessage =
        msg1 +
        ` Please ${
          this.userInfo && this.userInfo.sz_tier != "starter"
            ? this.getSZSupportLink(error)
            : ""
        } 
        `;
    } else {
      this.errorMessage =
        msg2 +
        ` or ${
          this.userInfo && this.userInfo.sz_tier != "starter"
            ? this.getSZSupportLink(error)
            : ""
        } `;
    }
  }

  getSZSupportLink(error) {
    return `<a href='https://illuminate.zendesk.com/hc/en-us/requests/new?ticket_form_id=21621854042395' target='_blank' class='alert-link'>
      contact support</a> ${
        error === 403
          ? " if you think this is an error."
          : " if this issue persists."
      }`;
  }

  getKPIData(id) {
    let url = `${environment.kpiServiceApi}/api/${environment.apiVersion}/profile/district/tile/${id}/latest.json`;
    return this.http
      .get(url, { withCredentials: true })
      .pipe(catchError((err) => this.handleError(err)));
  }

  getSchoolKPIData(schoolId, kpiId) {
    let url = `${environment.kpiServiceApi}/api/${environment.apiVersion}/profile/school/${schoolId}/tile/${kpiId}/latest.json`;
    return this.http
      .get(url, { withCredentials: true })
      .pipe(catchError((err) => this.handleError(err)));
  }

  getRealtimeSchoolKPIData(schoolId, kpiId) {
    let url = `${environment.kpiServiceApi}/api/v3/profile/school/${schoolId}/tile/${kpiId}/latest`;
    return this.http
      .get(url, { withCredentials: true })
      .pipe(catchError((err) => this.handleError(err)));
  }

  getSelectedMetricData(kpiId) {
    let url = `${environment.kpiServiceApi}/api/${environment.apiVersion}/profile/district/tile/${kpiId}/latest.json`;
    return this.http.get(url, { withCredentials: true });
  }

  getUserInfo() {
    if (this.userInfo) {
      // if `userInfo` is available just return it as `Observable`
      return observableOf(this.userInfo);
    } else if (this.userInfoPendingObservable) {
      // if `this.userInfoPendingObservable` is set then the request is in progress
      // return the `Observable` for the ongoing request
      return this.userInfoPendingObservable;
    } else {
      // create the request, store the `Observable` for subsequent subscribers
      let url = `${environment.kpiServiceApi}/userinfo/`;
      this.userInfoPendingObservable = this.http
        .get(url, { withCredentials: true })
        .pipe(
          tap((val: UserInfo) => {
            this.userInfo = val;

            //Set user attributes for Rollbar
            var rollbar = this.injector.get(RollbarService);
            rollbar.configure({
              payload: {
                person: {
                  id: val.userId,
                  customerId: val.organizationId as string,
                },
                environment: environment.mode == "production" ? "prod" : "dev",
              },
            });

            // Set attributes for Pendo - DEV AND PROD
            this.winRef.nativeWindow.pendo.initialize({
              excludeAllText: true,
              visitor: {
                id: val.userId,
                role: val.role,
                isRenaissance:
                  val.email && val.email.includes("@renaissance.com"),
                isSchoolzilla:
                  val.email && val.email.includes("@schoolzilla.com"),
              },
              account: {
                id: val.organizationId as string,
                name: val.customer_name,
                tier: val.sz_tier,
                tenant: val.rgp_tenant,
              },
            });

            // when the cached data is available we don't need the `Observable` reference anymore
            this.userInfoPendingObservable = null;
          }),
          // make it shared so more than one subscriber can get the result
          share(),
          catchError(this.handleError)
        );
      return this.userInfoPendingObservable;
    }
  }

  getStudentInfo(id) {
    if (this.studentInfo[id]) {
      // if `studentInfo` is available just return it as `Observable`
      return observableOf(this.studentInfo[id]);
    } else if (this.studentInfoPendingObservable) {
      // if `this.studentInfoPendingObservable` is set then the request is in progress
      // return the `Observable` for the ongoing request
      return this.studentInfoPendingObservable;
    } else {
      // create the request, store the `Observable` for subsequent subscribers
      let url = `${environment.kpiServiceApi}/studentinfo/${id}`;
      this.studentInfoPendingObservable = this.http
        .get(url, { withCredentials: true })
        .pipe(
          tap((val) => {
            this.studentInfo[id] = val;
            // when the cached data is available we don't need the `Observable` reference anymore
            this.studentInfoPendingObservable = null;
          }),
          // make it shared so more than one subscriber can get the result
          share(),
          catchError((error: any) => this.handleError(error))
        );
      return this.studentInfoPendingObservable;
    }
  }

  getSchoolInfo(id) {
    if (this.schoolInfo[id]) {
      // if `schoolInfo` is available just return it as `Observable`
      return observableOf(this.schoolInfo[id]);
    } else if (this.schoolInfoPendingObservable) {
      // if `this.schoolInfoPendingObservable` is set then the request is in progress
      // return the `Observable` for the ongoing request
      return this.schoolInfoPendingObservable;
    } else {
      // create the request, store the `Observable` for subsequent subscribers
      let url = `${environment.kpiServiceApi}/schoolinfo/${id}`;
      this.schoolInfoPendingObservable = this.http
        .get(url, { withCredentials: true })
        .pipe(
          tap((val) => {
            this.schoolInfo[id] = val;
            // when the cached data is available we don't need the `Observable` reference anymore
            this.schoolInfoPendingObservable = null;
          }),
          // make it shared so more than one subscriber can get the result
          share(),
          catchError((err) => this.handleError(err))
        );
      return this.schoolInfoPendingObservable;
    }
  }

  getDistrictProfileMetadata() {
    let url = `${environment.kpiServiceApi}/api/${environment.apiVersion}/profile/district/latest.json`;
    return this.http
      .get(url, { withCredentials: true })
      .pipe(catchError((err) => this.handleError(err)));
  }

  getSchoolComparisonMetadata() {
    let url = `${environment.kpiServiceApi}/api/${environment.apiVersion}/comparison/district/latest.json`;
    return this.http
      .get(url, { withCredentials: true })
      .pipe(catchError((err) => this.handleError(err)));
  }

  getSchoolStudentComparisonMetadata(schoolId) {
    let url = `${environment.kpiServiceApi}/api/${environment.apiVersion}/comparison/school/${schoolId}/latest.json`;
    return this.http
      .get(url, { withCredentials: true })
      .pipe(catchError((err) => this.handleError(err)));
  }

  getSchoolProfileById(schoolId) {
    let url = `${environment.kpiServiceApi}/api/${environment.apiVersion}/profile/school/${schoolId}/latest.json`;
    return this.http
      .get(url, { withCredentials: true })
      .pipe(catchError((err) => this.handleError(err)));
  }

  getStudentProfileBySystemSchoolId(schoolId) {
    let url = `${environment.kpiServiceApi}/api/v2/profile/school/${schoolId}/studentprofile.json`;
    return this.http
      .get(url, { withCredentials: true })
      .pipe(catchError((err) => this.handleError(err)));
  }

  getStudentKpiData(studentId, kpiId) {
    let url = `${environment.kpiServiceApi}/api/v2/profile/student/${studentId}/tile/${kpiId}/latest.json`;
    return this.http
      .get(url, { withCredentials: true })
      .pipe(catchError((err) => this.handleError(err)));
  }

  getDistrictSchools() {
    let url = `${environment.kpiServiceApi}/api/v2/profile/district/schools.json`;
    return this.http
      .get(url, { withCredentials: true })
      .pipe(catchError((err) => this.handleError(err)));
  }

  getDistrictAndSchoolsUsageData() {
    let url = `${environment.kpiServiceApi}/api/v2/profile/usage/all.json`;
    return this.http.get(url, { withCredentials: true });
  }

  getUserLoginData() {
    let url = `${environment.kpiServiceApi}/api/v2/profile/usage/users.json`;
    return this.http.get(url, { withCredentials: true });
  }

  getMosaicStudentViews() {
    let url = `${environment.kpiServiceApi}/studentviews`;
    return this.http.post(url, {}, { withCredentials: true });
  }

  getOurStudentsHeaderData() {
    let url = `${environment.kpiServiceApi}/api/${environment.apiVersion}/comparison/district/ourstudents/latest.json`;
    return this.http
      .get(url, { withCredentials: true })
      .pipe(catchError((err) => this.handleError(err)));
  }

  getOurStudentsHeaderDataForSchool(schoolId) {
    let url = `${environment.kpiServiceApi}/api/${environment.apiVersion}/comparison/school/${schoolId}/ourstudents/latest.json`;
    return this.http
      .get(url, { withCredentials: true })
      .pipe(catchError((err) => this.handleError(err)));
  }

  getStudentData(studentDataSearch: studentDataSearch) {
    let url = `${environment.kpiServiceApi}/studentdata/`;
    return this.http
      .post(url, studentDataSearch, { withCredentials: true })
      .pipe(catchError((err) => this.handleError(err)));
  }

  getGroups(role, schoolId) {
    let url = `${environment.kpiServiceApi}/studentgroups/search`;
    return this.http
      .post(
        url,
        { role: role, schoolIds: schoolId ? [schoolId] : null },
        { withCredentials: true }
      )
      .pipe(catchError((err) => this.handleError(err)));
  }

  addView(view) {
    let url = `${environment.kpiServiceApi}/myviews`;
    return this.http
      .post(url, view, { withCredentials: true })
      .pipe(catchError((err) => this.handleError(err)));
  }

  deleteView(view) {
    let url = `${environment.kpiServiceApi}/myviews/${view.id}`;
    return this.http
      .delete(url, { withCredentials: true })
      .pipe(catchError((err) => this.handleError(err)));
  }

  updateView(view) {
    let url = `${environment.kpiServiceApi}/myviews/${view.id}`;
    return this.http
      .put(url, view, { withCredentials: true })
      .pipe(catchError((err) => this.handleError(err)));
  }

  addGroup(group) {
    let url = `${environment.kpiServiceApi}/studentgroups/`;
    return this.http
      .post(url, group, { withCredentials: true })
      .pipe(catchError((err) => this.handleError(err)));
  }

  deleteGroup(group) {
    let url = `${environment.kpiServiceApi}/studentgroups/${group.id}`;
    return this.http
      .delete(url, { withCredentials: true })
      .pipe(catchError((err) => this.handleError(err)));
  }

  updateGroup(group) {
    let url = `${environment.kpiServiceApi}/studentgroups/${group.id}`;
    return this.http
      .put(url, group, { withCredentials: true })
      .pipe(catchError((err) => this.handleError(err)));
  }

  addStudentsToGroup(group, studentKeys) {
    let url = `${environment.kpiServiceApi}/studentgroups/${group.id}/addstudents`;
    return this.http
      .post(url, studentKeys, { withCredentials: true })
      .pipe(catchError((err) => this.handleError(err)));
  }

  removeStudentsFromGroup(group, studentKeys) {
    let url = `${environment.kpiServiceApi}/studentgroups/${group.id}/removestudents`;
    return this.http
      .post(url, studentKeys, { withCredentials: true })
      .pipe(catchError((err) => this.handleError(err)));
  }

  getOurSchoolsKpiData() {
    if (this.schoolKpiData) {
      // if `schoolKpiData` is available just return it as `Observable`
      return observableOf(this.schoolKpiData);
    } else if (this.schoolKpiDataPendingObservable) {
      // if `this.schoolKpiDataPendingObservable` is set then the request is in progress
      // return the `Observable` for the ongoing request
      return this.schoolKpiDataPendingObservable;
    } else {
      // create the request, store the `Observable` for subsequent subscribers
      let url = `${environment.kpiServiceApi}/api/v2/comparison/district/schoolkpis/latest.json`;
      this.schoolKpiDataPendingObservable = this.http
        .get(url, { withCredentials: true })
        .pipe(
          tap((val) => {
            this.schoolKpiData = val;
            // when the cached data is available we don't need the `Observable` reference anymore
            this.schoolKpiDataPendingObservable = null;
          }),
          // make it shared so more than one subscriber can get the result
          share(),
          catchError((err) => this.handleError(err))
        );
      return this.schoolKpiDataPendingObservable;
    }
  }

  getGoals(kpiId) {
    let url = `${environment.kpiServiceApi}/kpis/${kpiId}/goals`;
    return this.http.get(url, { withCredentials: true });
  }

  getUser() {
    let url = `${environment.kpiServiceApi}/userinfo/`;
    return this.http.get(url, { withCredentials: true });
  }

  getMetrics() {
    let url = `${environment.kpiServiceApi}/kpis?withRelationships=true&includeHidden=true`;
    return this.http.get(url, { withCredentials: true });
  }

  getDistrict() {
    let url = `${environment.kpiServiceApi}/custinfo/`;
    return this.http.get(url, { withCredentials: true });
  }

  saveGoals(kpiId, goals) {
    let url = `${environment.kpiServiceApi}/kpis/${kpiId}/goals`;
    return this.http.put(url, goals, { withCredentials: true });
  }

  updateMetricVisibility(body) {
    let url = `${environment.kpiServiceApi}/kpis/updatevisibility`;
    return this.http.post(url, body, { withCredentials: true });
  }

  saveMetricTitleDesc(kpiId, name, desc) {
    let url = `${environment.kpiServiceApi}/kpis/${kpiId}`;
    let body = {
      subtitle: name,
      definition: desc,
    };
    return this.http.patch(url, body, { withCredentials: true });
  }

  saveOtherReportsUrl(kpiId, otherReportsUrl) {
    let url = `${environment.kpiServiceApi}/kpis/${kpiId}`;
    let body = {
      otherReportsUrl,
    };
    return this.http.patch(url, body, { withCredentials: true });
  }

  setCurrentPage(currentPageFragment) {
    this.currentPage = environment.kpiServiceApi + currentPageFragment;
  }

  trackEvent(eventType, eventData?) {
    eventData = eventData ? eventData : {};
    let url = `${environment.kpiServiceApi}/api/events`;
    let body = {
      eventType: eventType,
      source: this.currentPage,
      eventData: eventData,
    };
    // track in Pendo for all non-page view events (page views are tracked separately in Pendo)
    if (this.userInfo && !eventType.includes("mosaic.pageview")) {
      this.winRef.nativeWindow.pendo.track(eventType, eventData);
    }
    return this.http
      .post(url, body, {
        withCredentials: true,
        responseType: "text",
      })
      .pipe(catchError((err) => this.handleError(err)));
  }

  trackChartClicked(chartType, kpiId) {
    return this.trackEvent("mosaic.click.chartButton.select", {
      chartType,
      metricId: kpiId,
    });
  }

  trackDatawallModalButtonClicked(kpiId) {
    return this.trackEvent("mosaic.click.datawallModalButton.select", {
      metricId: kpiId,
    });
  }

  trackFilterByCategorySelect(categoryName) {
    return this.trackEvent("mosaic.click.schoolFilterByCategory.select", {
      value: categoryName,
    });
  }

  trackFilterBySubcategorySelect(subCategory) {
    return this.trackEvent("mosaic.click.filterBySubcategory.select", {
      value: subCategory,
    });
  }

  trackStudentSelect() {
    return this.trackEvent("mosaic.click.student.selectOne", {});
  }

  trackAllStudentsSelect() {
    return this.trackEvent("mosaic.click.student.selectAll", {});
  }

  trackStudentGroupCriteriaAdded(groupId, studentKpiId) {
    return this.trackEvent("mosaic.click.studentGroupCriteria.add", {
      groupId: groupId,
      studentKpiId: studentKpiId,
    });
  }

  trackStudentGroupSelect(groupId) {
    return this.trackEvent("mosaic.click.studentGroup.select", {
      groupId: groupId,
    });
  }

  trackModalOpened(modalName, studentGroupSelected: boolean) {
    return this.trackEvent("mosaic.click.modal.open", {
      value: modalName,
      studentGroupSelected: studentGroupSelected,
    });
  }

  trackPageSizeSelect(pageSize) {
    return this.trackEvent("mosaic.click.pageSize.select", {
      pageSize: pageSize,
    });
  }

  trackOurStudentExportToCSVInitiated() {
    return this.trackEvent("mosaic.click.studentExportToCSV.initiated", {});
  }

  trackOurStudentExportToCSVSuccess() {
    return this.trackEvent("mosaic.click.studentExportToCSV.success", {});
  }

  trackNavButtonClicked(label) {
    return this.trackEvent("mosaic.click.nav.select", {
      label: label,
    });
  }

  trackMetricZoomContentTypeSelected(kpiId, contentType) {
    return this.trackEvent("mosaic.click.metricZoomContentType.select", {
      metricId: kpiId,
      value: contentType,
    });
  }

  trackMetricZoomContentSubtypeSelected(kpiId, selectedContentSubtypes) {
    return this.trackEvent("mosaic.click.metricZoomContentSubtype.select", {
      metricId: kpiId,
      values: selectedContentSubtypes,
    });
  }

  trackMetricZoomFilterBySelected(kpiId, selectedFilters) {
    return this.trackEvent("mosaic.click.metricZoomFilterBy.select", {
      metricId: kpiId,
      values: selectedFilters,
    });
  }

  trackMetricZoomCompareBySelected(kpiId, selectedCompareBy) {
    return this.trackEvent("mosaic.click.metricZoomCompareBy.select", {
      metricId: kpiId,
      value: selectedCompareBy,
    });
  }

  trackMetricZoomTimePeriodSelected(kpiId, clickedTimePeriod) {
    return this.trackEvent("mosaic.click.metricZoomTimePeriods.select", {
      metricId: kpiId,
      value: clickedTimePeriod,
    });
  }

  trackMetricZoomTableSelected(kpiId) {
    return this.trackEvent("mosaic.click.metricZoomTable.select", {
      metricId: kpiId,
    });
  }

  trackMetricZoomGraphSelected(kpiId) {
    return this.trackEvent("mosaic.click.metricZoomGraph.select", {
      metricId: kpiId,
    });
  }

  trackMetricZoomOtherReportsClicked() {
    return this.trackEvent("mosaic.click.otherReportsModalButton.select", {});
  }

  trackMetricZoomTrendClicked(kpiId) {
    return this.trackEvent("mosaic.click.metricZoomTrendButton.select", {
      metricId: kpiId,
    });
  }

  trackMetricZoomDistributionClicked(kpiId) {
    return this.trackEvent("mosaic.click.metricZoomDistributionButton.select", {
      metricId: kpiId,
    });
  }

  trackMetricZoomExportToCSVInitiated(kpiId) {
    return this.trackEvent("mosaic.click.metricZoomExportToCSV.initiated", {
      metricId: kpiId,
    });
  }

  trackMetricZoomExportToCSVSuccess(kpiId) {
    return this.trackEvent("mosaic.click.metricZoomExportToCSV.success", {
      metricId: kpiId,
    });
  }

  trackMetricZoomExportToPNGInitiated(kpiId) {
    return this.trackEvent("mosaic.click.metricZoomExportToPNG.initiated", {
      metricId: kpiId,
    });
  }

  trackOurStudentsSchoolSelect() {
    return this.trackEvent("mosaic.click.ourStudentsSchool.select", {});
  }

  trackOurStudentsMultiDemographicSelect() {
    return this.trackEvent(
      "mosaic.click.ourStudentsMultiDemographic.select",
      {}
    );
  }

  trackOurStudentsTeacherSectionSelect() {
    return this.trackEvent("mosaic.click.ourStudentsTeacherSection.select", {});
  }

  trackOurStudentsListSelect() {
    return this.trackEvent("mosaic.click.ourStudentsList.select", {});
  }

  trackStudentProfileExpandPanelSelect(studentKpiId) {
    return this.trackEvent("mosaic.click.studentProfileExpandPanel.select", {
      studentKpiId: studentKpiId,
    });
  }

  trackStudentProfileCollapsePanelSelect(studentKpiId) {
    return this.trackEvent("mosaic.click.studentProfileCollapsePanel.select", {
      studentKpiId: studentKpiId,
    });
  }

  trackStudentProfileTableTimePeriodSelect() {
    return this.trackEvent(
      "mosaic.click.studentProfileTableTimePeriod.select",
      {}
    );
  }

  trackOurStudentsDistChartClick(studentKpiId) {
    return this.trackEvent("mosaic.click.ourStudentsDistChart.select", {
      studentKpiId: studentKpiId,
    });
  }

  trackDownloadTileImageClick(kpiId, chartType) {
    return this.trackEvent("mosaic.click.downloadTileImage.select", {
      chartType,
      metricId: kpiId,
    });
  }

  getCSV(studentDataSearch: studentDataSearch, totalCount?: number) {
    // send off pendo tracking event
    if (this.userInfo) {
      this.winRef.nativeWindow.pendo.track("Our Students CSV Export", {
        numRecords: totalCount,
        studentGroupId: studentDataSearch
          ? studentDataSearch.studentGroupId
          : null,
      });
    }
    let url = `${environment.kpiServiceApi}/studentdata/csv`;
    return this.http.post(url, studentDataSearch, {
      withCredentials: true,
      responseType: "blob",
    });
  }

  realtimeMetricCalculation(kpiId, calcParams) {
    return this.http
      .post(`${environment.kpiServiceApi}/calculate/${kpiId}`, calcParams, {
        withCredentials: true,
      })
      .pipe(catchError((err) => this.handleError(err)));
  }

  getStudentGrades(studentKpiId, studentKey) {
    let url = `${environment.kpiServiceApi}/studentgrades/${studentKpiId}/${studentKey}`;
    return this.http
      .get(url, { withCredentials: true })
      .pipe(catchError((err) => this.handleError(err)));
  }

  getStudentCreditsEarned(studentKpiId, studentKey) {
    let url = `${environment.kpiServiceApi}/studentcreditsearned/${studentKpiId}/${studentKey}`;
    return this.http
      .get(url, { withCredentials: true })
      .pipe(catchError((err) => this.handleError(err)));
  }

  getStudentPhoto(studentKey) {
    let url = `${environment.kpiServiceApi}/students/${studentKey}/photo`;
    return this.http
      .get(url, {
        withCredentials: true,
        responseType: "blob",
      })
      .pipe(catchError((err) => this.handlePhotoError(err)));
  }

  getGraduationRequirements() {
    let url = `${environment.kpiServiceApi}/datawarehouse/grades/graduationrequirements`;
    return this.http
      .get(url, { withCredentials: true })
      .pipe(catchError((err) => this.handleError(err)));
  }

  updateGraduationPlan(gradPlanId) {
    let url = `${environment.kpiServiceApi}/datawarehouse/grades/graduationrequirements/graduationplanid/${gradPlanId}/configure`;
    return this.http
      .post(url, {}, { withCredentials: true })
      .pipe(catchError((err) => this.handleError(err)));
  }

  handlePhotoError(error) {
    if (error.status !== 403 && error.status !== 404) {
      return this.handleError(error);
    } else {
      return observableOf(null);
    }
  }

  handleError(error) {
    this.studentInfoPendingObservable = null;
    if (error.status === 401) {
      if (this.userInfo && this.userInfo.rgp_szlogin) {
        console.error("Not logged in! RGP redirect...");
        window.location.href = this.userInfo.rgp_szlogin;
      } else {
        console.error("Not logged in!  Redirecting to login page...");
        window.location.href = `${environment.szAppHostname}`;
      }
    }
    this.rollbarErrorHandler.handleError(error);
    return observableThrowError(error.status);
  }
}

export interface UserInfo {
  userId: string;
  customer_name: string;
  role: string;
  organizationId: string;
  email: string;
  rgp_tenant: string;
  sz_tier: string;
}
